/* Copyright (c) 2021-2026, NVIDIA CORPORATION.  All rights reserved.
*
* NVIDIA CORPORATION and its licensors retain all intellectual property
* and proprietary rights in and to this software, related documentation
* and any modifications thereto.  Any use, reproduction, disclosure or
* distribution of this software and related documentation without an express
* license agreement from NVIDIA CORPORATION is strictly prohibited.
*/

/** This file was automatically generated. DO NOT MODIFY IT BY HAND. */

/* eslint-disable @typescript-eslint/naming-convention */

export module StreamingTypes {

/** This object was automatically generated by json-schema-to-typescript. DO NOT MODIFY IT BY HAND. */

/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "PrefilterMode".
 */
export type PrefilterMode = 0 | 1 | 2;
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "ChromaFormat".
 */
export type ChromaFormat = 0 | 1;
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "HudStreamingMode".
 */
export type HudStreamingMode = 0 | 1 | 2;

export interface Streaming {
    [k: string]: any;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "SpanData".
 */
export interface SpanData {
    [k: string]: string;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "RemoteConfigData".
 */
export interface RemoteConfigData {
    data?: data;
}
export interface data {
    [k: string]: any;
}
/**
 * HDR10+Gaming-specific fields.
 *
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "Hdr10PlusGamingData".
 */
export interface Hdr10PlusGamingData {
    version?: number;
    peakLuminanceIndex?: number;
    peakFullFrameLuminanceIndex?: number;
}
/**
 * DLSS overrides for the streaming session.
 *
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "DlssOverrides".
 */
export interface DlssOverrides {
    srMode?: number;
    srModel?: number;
    srScale?: number;
    rrModel?: number;
    fgMode?: number;
    fgModel?: number;
    fgMultiplier?: number;
}
/**
 * DLSS state as reported by the server via NVB_EVT_DLSS_STATE_CHANGE_EVENT.
 *
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "DlssState".
 */
export interface DlssState {
    /**
     * Whether the DLSS super resolution override is active.
     */
    superResolutionActive: boolean;
    /**
     * Whether the DLSS ray reconstruction override is active.
     */
    rayReconstructionActive: boolean;
    /**
     * Whether the DLSS frame generation override is active.
     */
    frameGenerationActive: boolean;
    /**
     * The frame generation multiplier that is currently active.
     */
    activeFgMultiplier: number;
    overrides: DlssOverrides;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "StreamingProfile".
 */
export interface StreamingProfile {
    name: string;
    description: string;
    width: number;
    height: number;
    fps: number;
    maxBitrate: number;
    maxBitrateAuto?: boolean;
    dataUsage: number;
    dynamicStreamingMode: number;
    vSync: number;
    aspectRatio?: string;
    maxPacketSize?: number;
    hdr10PlusGamingData?: Hdr10PlusGamingData;
    hdrStreamingMode?: HdrStreamingMode;
    reflex?: boolean;
    cloudGsync?: boolean;
    l4sEnabled?: boolean;
    bitDepth?: number;
    supportedHidDevices?: number;
    profile?: number;
    displayDpi?: number;
    fallbackToLogicalResolution?: boolean;
    prefilter?: PrefilterMode;
    sharpen?: number;
    denoise?: number;
    chromaFormat?: ChromaFormat;
    hudStreamingMode?: HudStreamingMode;
    codec?: number;
    dlssOverrides?: DlssOverrides;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "FinalizedStreamingFeatures".
 */
export interface FinalizedStreamingFeatures {
    reflex?: boolean;
    bitDepth?: number;
    cloudGsync?: boolean;
    l4sEnabled?: boolean;
    supportedHidDevices?: number;
    profile?: number;
    fallbackToLogicalResolution?: boolean;
    chromaFormat?: ChromaFormat;
    prefilter?: PrefilterMode;
    sharpen?: number;
    denoise?: number;
    hudStreamingMode?: HudStreamingMode;
}
/**
 * Content rating information (ESRB/PEGI/etc...)
 *
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "ContentRating".
 */
export interface ContentRating {
    /**
     * Rating system type (ESRB/PEGI/etc..)
     */
    ratingSystem?: string;
    /**
     * Rating class (Everyone/Teen/Mature/etc..) as an opaque enum value (enum list is based on rating type)
     */
    categoryKey: string;
    /**
     * List of content descriptors as an array of opaque enum values (enum list is based on rating type)
     */
    contentDescriptorKeys: string[];
    /**
     * List of interactive element keys as an array of opaque enum values (enum list is based on rating type)
     */
    interactiveElementKeys: string[];
    /**
     * Rating class (Everyone/Teen/Mature/etc..) as a user displayable string
     */
    ratingClassText: string;
    /**
     * The list of content descriptors, seperated by commas, as a user displayable string
     */
    contentDescriptorText: string;
    /**
     * Large rating icon url
     */
    largeImageUrl: string;
    /**
     * small rating icon url
     */
    smallImageUrl: string;
    /**
     * Interval at which to display the rating information in minutes
     */
    displayIntervalMin: number;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "StartParameters".
 */
export interface StartParameters {
    appId: number;
    appLaunchMode?: number;
    frameStatsEnabled: boolean;
    summaryStatsEnabled: boolean;
    maxLocalPlayers?: number;
    advancedLatencyOptimization: boolean;
    networkPacketCaptureEnabled?: boolean;
    session?: string;
    metaData?: MetadataField;
    /**
     * Settings for GridApp frame loss detection algorithm.
     * It's added for MVPC-20867 to support StartStreaming command that skips the prepare call.
     * Frame loss detection logic is currently in the cef in grid app, but it needs to be moved out eventually.
     */
    frameLossWarningTimeout?: number;
    /**
     * Settings for GridApp frame loss detection algorithm.
     * It's added for MVPC-20867 to support StartStreaming command that skips the prapare call.
     * Frame loss detection logic is currently in the cef in grid app, but it needs to be moved out eventually.
     */
    frameLossErrorTimeout?: number;
    spanData?: SpanData;
    keyboardLayout?: string;
    allowKeyboardLayoutChange?: boolean;
    locale?: string;
    partnerCustomData?: string;
    address?: string;
    serverType?: number;
    port?: number;
    streamingProfile?: StreamingProfile;
    accountLinked?: boolean;
    persistingInGameSettings?: boolean;
    gameShortName?: string;
    networkSessionId: string;
    heroImage?: string;
    gameDisplayOwnRating?: boolean;
    storeName?: string;
    appName?: string;
    subscriptionLongDesc?: string;
    providerName?: string;
    audioModeFormat: number;
    supportedControls: string[];
    contentRating?: ContentRating;
    zoneName?: string;
    userAge?: number;
    serverLocation?: string;
    gpuNameMap?: gpuNameMap;
    streamingDisplayDataInfo?: StreamingDisplayDataInfo;
    remoteControllersBitmap?: number;
    refreshAuthToken?: boolean;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "MetadataField".
 */
export interface MetadataField {
    [k: string]: string;
}
export interface gpuNameMap {
    [k: string]: any;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "StreamingDisplayDataInfo".
 */
export interface StreamingDisplayDataInfo {
    desiredContentMaxLuminance?: number;
    desiredContentMinLuminance?: number;
    desiredContentMaxFrameAverageLuminance?: number;
}
/**
 * A single header (key-value pair)
 *
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "ClientHeader".
 */
export interface ClientHeader {
    headerName: string;
    headerValue: string;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "PrepareParameters".
 */
export interface PrepareParameters {
    address: string;
    serverType: number;
    port: number;
    profile: number;
    deviceId: string;
    advancedLatencyOptimization: boolean;
    streamingProfile?: StreamingProfile;
    cmsId?: string;
    appName?: string;
    shortName?: string;
    keyboardLayout?: string;
    locale?: string;
    clientAppVersion: string;
    clientHeaders: ClientHeader[];
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "SetAuthInfoParameters".
 */
export interface SetAuthInfoParameters {
    token?: string;
    tokenType: number;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "SetAuthTokenParameters".
 */
export interface SetAuthTokenParameters {
    token: string;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "SessionConfiguration".
 */
export interface SessionConfiguration {
    [k: string]: any;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "StopParameters".
 */
export interface StopParameters {
    session?: string;
    reasonCode?: number;
    spanData?: SpanData;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "GetActiveSessionsParameters".
 */
export interface GetActiveSessionsParameters {
    spanData?: SpanData;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "GetSessionInfoParameters".
 */
export interface GetSessionInfoParameters {
    session: string;
    spanData: SpanData;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "ResumeParameters".
 */
export interface ResumeParameters {
    networkSessionId: string;
    spanData?: SpanData;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "PauseStreamingParameters".
 */
export interface PauseStreamingParameters {
    reasonCode?: number;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "UpdateRemoteConfigParameters".
 */
export interface UpdateRemoteConfigParameters {
    overrideData?: RemoteConfigData;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "OpenOsrParameters".
 */
export interface OpenOsrParameters {
    enableInput: boolean;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "OsrRegisterCustomKeyPressParameters".
 */
export interface OsrRegisterCustomKeyPressParameters {
    name: string;
    keyCombination: string;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "OsrUnregisterCustomKeyPressParameters".
 */
export interface OsrUnregisterCustomKeyPressParameters {
    keyCombination: string;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "MagnificationFilterSupportDetail".
 */
export interface MagnificationFilterSupportDetail {
    /**
     * Id of filter
     */
    filterId: number;
    /**
     * Whether supported or not
     */
    MagnificationFilterSupport: boolean;
    /**
     * FrameFilterErrorInfo code from Geronimo
     */
    FrameFilterErrorInfo: number;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "GetMagnificationFilterSupportListReturn".
 */
export interface GetMagnificationFilterSupportListReturn {
    filterSupportList: MagnificationFilterSupportDetail[];
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "MagnificationFilterSupportParam".
 */
export interface MagnificationFilterSupportParam {
    /**
     * Id of filter
     */
    filterId: number;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "GetMagnificationFilterSupportListParameters".
 */
export interface GetMagnificationFilterSupportListParameters {
    filterSupportList: MagnificationFilterSupportParam[];
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "SetKeyboardLayoutParameters".
 */
export interface SetKeyboardLayoutParameters {
    kbLayout: string;
    allowKeyboardLayoutChange: boolean;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "ConfigureStreamingSettingsParameters".
 */
export interface ConfigureStreamingSettingsParameters {
    overrideData?: string;
    isInternalUser?: boolean;
    gxtOverrideData?: string;
    clientConfigOverride?: string;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "SendUnicodeParameters".
 */
export interface SendUnicodeParameters {
    unicodeInput: number[];
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "DisplayModeResolution".
 */
export interface DisplayModeResolution {
    width: number;
    height: number;
    refreshRate: number;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "DisplayModeReturn".
 */
export interface DisplayModeReturn {
    logical: DisplayModeResolution;
    physical: DisplayModeResolution;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "UpdateEventInfoGuidsParameters".
 */
export interface UpdateEventInfoGuidsParameters {
    systemInfoGuid: string;
    streamingProfileGuid: string;
    networkTestSessionId?: string;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "MediaConnectionInfo".
 */
export interface MediaConnectionInfo {
    ip?: string;
    port?: number;
    protocol?: number;
    usage?: number;
    appLevelProtocol?: number;
    resourcePath?: number;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "SignalConnectionInfo".
 */
export interface SignalConnectionInfo {
    ip?: string;
    port?: number;
    protocol?: string;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "GetAutoResolutionResult".
 */
export interface GetAutoResolutionResult {
    height: number;
    width: number;
    frameRate: number;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "GetAutoResolutionParameters".
 */
export interface GetAutoResolutionParameters {
    resolutions: Resolution[];
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "Resolution".
 */
export interface Resolution {
    profileName: number;
    width: number;
    height: number;
    fps: number[];
    enabled: boolean;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "ConnectedGamepadInfo".
 */
export interface ConnectedGamepadInfo {
    vendorId: number;
    productId: number;
    name: string;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "StartStreamingParameters".
 */
export interface StartStreamingParameters {
    sessionType?: SessionType;
    cmsId?: string;
    appLaunchMode?: number;
    maxLocalPlayers?: number;
    zoneName?: string;
    zoneAddress?: string;
    serverType?: string;
    accountLinked?: boolean;
    gameShortName?: string;
    sessionId?: string;
    subSessionId?: string;
    heroImage?: string;
    gameDisplayOwnRating?: boolean;
    storeName?: string;
    appName?: string;
    subscriptionLongDesc?: string;
    providerName?: string;
    audioModeFormat: number;
    resumeType?: ResumeType;
    streamingProfile?: StreamingProfile;
    finalizedStreamingFeatures?: FinalizedStreamingFeatures;
    contentRating?: ContentRating;
    eventInfoGuids: UpdateEventInfoGuidsParameters;
    signalConnectionInfo?: SignalConnectionInfo;
    mediaConnectionInfo?: MediaConnectionInfo[];
    configInfo?: ConfigureStreamingSettingsParameters;
    hostName?: string;
    serverLocation?: string;
    gpuName?: string;
    gpuType?: string;
    keyboardLayout?: string;
    allowKeyboardLayoutChange?: boolean;
    locale?: string;
    clientAppVersion?: string;
    supportedControls?: string[];
    frameStatsEnabled?: boolean;
    summaryStatsEnabled?: boolean;
    frameLossWarningTimeout?: number;
    frameLossErrorTimeout?: number;
    /**
     * JSON-serialized Ragnarok Session. Forwarded verbatim to Nsk::getStreamStartParameters.
     */
    serializedSession?: string;
    /**
     * Optional JSON-serialized ModeSelection result. Forwarded as-is to Nsk::getStreamStartParameters.
     */
    serializedModeSelection?: string;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "FormattedEventDetail".
 */
export interface FormattedEventDetail {
    name: string;
    ts: string;
    parameters: parameters;
}
export interface parameters {
    [k: string]: any;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "JsEventsCommonData".
 */
export interface JsEventsCommonData {
    clientId?: string;
    clientVer?: string;
    eventSchemaVer?: string;
    eventSysVer?: string;
    eventProtocol?: string;
    deviceId?: string;
    userId?: string;
    externalUserId?: string;
    idpId?: string;
    sessionId?: string;
    platform?: string;
    sentTs?: string;
    events?: FormattedEventDetail[];
    gdprFuncOptIn?: string;
    gdprTechOptIn?: string;
    gdprBehOptIn?: string;
    deviceGdprFuncOptIn?: string;
    deviceGdprTechOptIn?: string;
    deviceGdprBehOptIn?: string;
    deviceOS?: string;
    deviceOSVersion?: string;
    deviceType?: string;
    deviceModel?: string;
    deviceMake?: string;
    clientType?: string;
    clientVariant?: string;
    integrationId?: string;
    browserType?: string;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "UpdateEventDataElementsParameters".
 */
export interface UpdateEventDataElementsParameters {
    commonData?: JsEventsCommonData;
    config?: config;
    telemetryEventIds?: UpdateEventInfoGuidsParameters;
}
export interface config {
    server: string;
    version: string;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "PlatformGetMaxBitRateForArrayParameters".
 */
export interface PlatformGetMaxBitRateForArrayParameters {
    resolutions: {
        width: number;
        height: number;
        frameRate: number;
    }[];
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "PlatformGetMaxBitRateForArrayReturn".
 */
export interface PlatformGetMaxBitRateForArrayReturn {
    resolutions: {
        width: number;
        height: number;
        fps: number;
        maxBandwidth: number;
    }[];
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "IsPlatformSupportsHDRStreamingReturn".
 */
export interface IsPlatformSupportsHDRStreamingReturn {
    supportsHdrDecode: boolean;
    supportsHdrRendering: boolean;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "StreamingCodecNotificationReturn".
 */
export interface StreamingCodecNotificationReturn {
    codec: Codec;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "StreamingL4SStateNotificationReturn".
 */
export interface StreamingL4SStateNotificationReturn {
    l4sState: L4SState;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "IsPlatformSupportFpsParameters".
 */
export interface IsPlatformSupportFpsParameters {
    frameRates: {
        value: number;
    }[];
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "IsPlatformSupportFpsReturn".
 */
export interface IsPlatformSupportFpsReturn {
    frameRates: {
        value: number;
        supported: boolean;
    }[];
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "CalculateDataUsageParameters".
 */
export interface CalculateDataUsageParameters {
    width: number;
    height: number;
    frameRate: number;
    bitRateKbps: number;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "UpdateAdStateParameters".
 */
export interface UpdateAdStateParameters {
    adId: string;
    adAction: AdAction;
    clientTimestamp: number;
    cancelReason?: string;
    watchedTimeInMs?: number;
    pausedTimeInMs?: number;
    adName?: string;
    adDuration?: number;
    eventName?: string;
    eventDescription?: string;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "CloudGsyncDependenciesReturn".
 */
export interface CloudGsyncDependenciesReturn {
    osSupportsCloudGsync: boolean;
    driverSupportsCloudGsync: boolean;
    minOSVersionForCloudGsync?: string;
    minDriverVersionForCloudGsync?: string;
    minFpsForCloudGsync: number;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "GetPlatformCloudGsyncCapabilitiesReturn".
 */
export interface GetPlatformCloudGsyncCapabilitiesReturn {
    platformSupportsCloudGsync: boolean;
    isVrrCapableDisplay: boolean;
    isGsyncDisplay: boolean;
    cloudGsyncDependencies: CloudGsyncDependenciesReturn;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "GetDeviceCapabilitiesParameters".
 */
export interface GetDeviceCapabilitiesParameters {
    supportedModes: {
        width: number;
        height: number;
        fps: number;
    }[];
    forceRefresh?: boolean;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "RegisterCallbackReturn".
 */
export interface RegisterCallbackReturn {
    [k: string]: any;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "OsrCheckKeyPressCombinationParameters".
 */
export interface OsrCheckKeyPressCombinationParameters {
    keyCombination?: string;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "OsrRegisterKeyPressCallbackParameters".
 */
export interface OsrRegisterKeyPressCallbackParameters {
    osType?: string;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "OsrSetOverlayResolutionParameters".
 */
export interface OsrSetOverlayResolutionParameters {
    width: number;
    height: number;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "GetStreamStatsReturn".
 */
export interface GetStreamStatsReturn {
    streamStats?: {
        /**
         * average stream frame rate(fps) since last SummaryStats update
         */
        frameRate?: number;
        /**
         * Average game frame rate(fps) since last SummaryStats update
         */
        avgGameFrameRate?: number;
        /**
         * average difference time between desired frame rate and packet receipt time
         */
        packetJitterUs?: number;
        /**
         * number of frames dropped
         */
        droppedFrames?: number;
        /**
         * The aggregate number of lost video packets from server to client.
         */
        packetloss?: number;
        /**
         * The estimated round trip delay between server & client
         */
        roundTripDelayMs?: number;
        /**
         * The estimated bandwidth utilization from server to client.
         */
        bandwidthUtilization?: number;
        /**
         * available bandwidth for streaming in Kbps, this value doesnt represent the current total network bandwidth
         */
        bandwidth?: number;
        /**
         * Stream Quality Score
         */
        streamQualityScore?: number;
        /**
         * stream video Bitrate
         */
        streamBitrate?: number;
        /**
         * frame width
         */
        frameWidth?: number;
        /**
         * Frame Height
         */
        frameHeight?: number;
        /**
         * Index of the stream
         */
        streamIndex?: number;
        /**
         * System cpu usage
         */
        cpuUsage?: number;
        /**
         * Server Region
         */
        serverRegion?: string;
        /**
         * Server Zone
         */
        serverZone?: string;
        /**
         * Codec Name
         */
        codec?: string;
        /**
         * Color bit depth
         */
        bitDepth?: number;
        /**
         * Game is in SDR or HDR mode
         */
        sdrHdrMode?: number;
        /**
         * Game is in HDR10+Gaming mode
         */
        hdr10PlusGaming?: boolean;
        dlssState?: DlssState;
    };
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "SetStreamVolumeParameters".
 */
export interface SetStreamVolumeParameters {
    volume?: number;
    rampDurationSeconds?: number;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "SetMagnificationFilterParameters".
 */
export interface SetMagnificationFilterParameters {
    /**
     * Id of filter 1 for Lanczos or 2 for DISP
     */
    filterId: number;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "GetMagnificationFilterSupportParameters".
 */
export interface GetMagnificationFilterSupportParameters {
    /**
     * Id of filter 1 for Lanczos or 2 for DISP
     */
    filterId: number;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "GetMagnificationFilterSupportReturn".
 */
export interface GetMagnificationFilterSupportReturn {
    /**
     * Whether supported or not
     */
    MagnificationFilterSupport?: boolean;
    /**
     * FrameFilterErrorInfo code from Geronimo
     */
    FrameFilterErrorInfo?: number;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "SetMagnificationFilterSharpnessParameters".
 */
export interface SetMagnificationFilterSharpnessParameters {
    sharpness: Sharpness;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "ToggleUserInputParameters".
 */
export interface ToggleUserInputParameters {
    enable: boolean;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "ToggleJoystickMouseParameters".
 */
export interface ToggleJoystickMouseParameters {
    enable: boolean;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "SetVirtualKeyboardStateParameters".
 */
export interface SetVirtualKeyboardStateParameters {
    visible: boolean;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "SetVideoTransformsParameters".
 */
export interface SetVideoTransformsParameters {
    offsetX: number;
    offsetY: number;
    zoomFactor: number;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "RegisterGamepadChangedCallbackReturn".
 */
export interface RegisterGamepadChangedCallbackReturn {
    gamepadId: number;
    isConnected: boolean;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "GetConnectedGamepadsReturn".
 */
export interface GetConnectedGamepadsReturn {
    connectedGamepadInfoList: ConnectedGamepadInfo[];
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "RegisterGamepadEventCallbackParameters".
 */
export interface RegisterGamepadEventCallbackParameters {
    minPositiveMovement: number;
    maxNegativeMovement: number;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "RegisterGamepadEventCallbackReturn".
 */
export interface RegisterGamepadEventCallbackReturn {
    gamepadId?: number;
    control?: number;
    value?: number;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "RegisterMicCallbackReturn".
 */
export interface RegisterMicCallbackReturn {
    uiMicState: string;
    internalMicState: string;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "SetMicStateParameters".
 */
export interface SetMicStateParameters {
    enable: boolean;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "SetVsyncEnabledParameters".
 */
export interface SetVsyncEnabledParameters {
    enabled: boolean;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "SetAdaptiveFramePacingEnabledParameters".
 */
export interface SetAdaptiveFramePacingEnabledParameters {
    enabled: boolean;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "SetHudStateParameters".
 */
export interface SetHudStateParameters {
    enabled: boolean;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "SetDynamicStreamingModesStateParameters".
 */
export interface SetDynamicStreamingModesStateParameters {
    streamIdx: number;
    stateValue: number;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "SetPreFilterStateParameters".
 */
export interface SetPreFilterStateParameters {
    mode: number;
    denoiseLevel: number;
    sharpnessLevel: number;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "SetStreamingMaxBitrateParameters".
 */
export interface SetStreamingMaxBitrateParameters {
    streamIdx: number;
    kbps: number;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "SetIdleTimeoutParameters".
 */
export interface SetIdleTimeoutParameters {
    seconds: number;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "RegisterGridServerAnalyticsCallbackReturn".
 */
export interface RegisterGridServerAnalyticsCallbackReturn {
    [k: string]: any;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "SendClipboardUpdateParameters".
 */
export interface SendClipboardUpdateParameters {
    /**
     * Value format json schema: //sw/gcomp/dev/common/protocols/json/schema_client_messages/v1/js_message.schema.json
     */
    clipboardInfo: string;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "AuthorizeAVDevicesParameters".
 */
export interface AuthorizeAVDevicesParameters {
    mic?: boolean;
    camera?: boolean;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "SendKeyEventParameters".
 */
export interface SendKeyEventParameters {
    keyEvent: {
        [k: string]: any;
    };
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "ToggleISSOStatsParameters".
 */
export interface ToggleISSOStatsParameters {
    enableIsso: boolean;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "CalculateDataUsageReturn".
 */
export interface CalculateDataUsageReturn {
    dataUsage: number;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "SetStreamingL4sStateParameters".
 */
export interface SetStreamingL4sStateParameters {
    streamIdx: number;
    enabled: boolean;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "PrefetchDeviceCapabilitiesParameters".
 */
export interface PrefetchDeviceCapabilitiesParameters {
    forceRefresh?: boolean;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "SetClientSupportsVirtualGamepadParameters".
 */
export interface SetClientSupportsVirtualGamepadParameters {
    supportsVirtualGamepad: boolean;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "SetSupportedHidTypesParameters".
 */
export interface SetSupportedHidTypesParameters {
    supportedControls: string[];
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "SetMicRecordingEnabledParameters".
 */
export interface SetMicRecordingEnabledParameters {
    enabled: boolean;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "OpenLinkInExternalBrowserParameters".
 */
export interface OpenLinkInExternalBrowserParameters {
    url: string;
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "UpdateVirtualGamepadInputParameters".
 */
export interface UpdateVirtualGamepadInputParameters {
    buttons: number;
    trigger: number;
    axes: number[];
}

/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "HdrStreamingMode".
 */
export enum HdrStreamingMode {
    OFF = 'OFF',
    HDR = 'HDR',
    HDR10_PLUS_GAMING = 'HDR10_PLUS_GAMING'
}
/**
 * This interface was referenced by `Streaming`'s JSON-Schema
 * via the `definition` "ResumeType".
 */
export enum ResumeType {
    AUTO = 'AUTO',
    MANUAL = 'MANUAL',
    NONE = 'NONE'
}
export enum SessionType {
    Launch = 'Launch',
    Resume = 'Resume'
}
export enum Codec {
    H264 = 'H264',
    H265 = 'H265',
    AV1 = 'AV1',
    None = 'None'
}
export enum L4SState {
    UNKNOWN = 0,
    INACTIVE = 1,
    ACTIVE = 2
}
export enum AdAction {
    UNKNOWN = 0,
    START = 1,
    PAUSE = 2,
    RESUME = 3,
    FINISH = 4,
    CANCEL = 5,
    STATE_CHANGE_EVENT = 6
}
/**
 * sharpness name in string
 */
export enum Sharpness {
    Low = 'Low',
    Medium = 'Medium',
    High = 'High',
    Off = 'Off'
}


}
